import React, { useCallback, useEffect, useState } from "react";
import { Group, Badge, ActionIcon, Text } from "@mantine/core";
import { MdRefresh } from "react-icons/md";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { useMsal } from "@azure/msal-react";

import { getToken, useLogin } from "../../authConfig";
import { getTouchpointAnalyticsApi, TouchpointAnalyticsData, TouchpointAnalyticsResponse } from "../../api";
import { ChartScaffold, DateRangeControl, CountryFilterControl, DomainFilterControl, createDefaultDateRange, formatDateRange } from "./shared";
import { TimezoneIndicator } from "../common/TimezoneIndicator";
import { TimezoneUtils } from "../../utils/timezoneUtils";

export interface TouchpointChartProps {
    title?: string;
    height?: number;
    showTitle?: boolean;
    showLegend?: boolean;
    showStats?: boolean;
    entraOid?: string; // Filter for specific user
    defaultDaysBack?: number; // Number of days back for default date range
    colors?: string[]; // Array of colors for the pie slices
}

export const TouchpointChart: React.FC<TouchpointChartProps> = ({
    title = "Messages by Touchpoint",
    height = 400,
    showTitle = true,
    showLegend = true,
    showStats = true,
    entraOid,
    defaultDaysBack = 7,
    colors = ["#339af0", "#51cf66", "#ff8cc8", "#ffd43b", "#ff6b6b", "#9775fa", "#40c057", "#fd7e14", "#e64980", "#12b886"]
}) => {
    const [data, setData] = useState<TouchpointAnalyticsData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalMessages, setTotalMessages] = useState<number>(0);
    const [totalTouchpoints, setTotalTouchpoints] = useState<number>(0);
    const [availableCountries, setAvailableCountries] = useState<string[]>([]);
    const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
    const [availableDomains, setAvailableDomains] = useState<string[]>([]);
    const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
    const [sqlQuery, setSqlQuery] = useState<string | undefined>();

    const client = useLogin ? useMsal().instance : undefined;

    const [dateRange, setDateRange] = useState<[Date, Date]>(() => createDefaultDateRange(defaultDaysBack));

    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const idToken = client ? await getToken(client) : undefined;
            if (!idToken) {
                throw new Error("No idToken available for fetching analytics");
            }

            const [start, end] = dateRange;
            const userTimezone = TimezoneUtils.getUserTimezone();
            const startDate = TimezoneUtils.formatDateForAPI(start);
            const endDate = TimezoneUtils.formatDateForAPI(end);
            const countries = selectedCountries.length > 0 ? selectedCountries : undefined;
            const domains = selectedDomains.length > 0 ? selectedDomains : undefined;

            const result = await getTouchpointAnalyticsApi(idToken, startDate, endDate, userTimezone, countries, entraOid, domains);

            setData(result.data || []);
            setTotalMessages(result.total_messages || 0);
            setTotalTouchpoints(result.total_touchpoints || 0);
            setAvailableCountries(result.available_countries || []);
            setAvailableDomains(result.available_domains || []);
            setSqlQuery(result.sql_query);
        } catch (chartError) {
            console.error("Error loading touchpoint analytics:", chartError);
            setError(chartError instanceof Error ? chartError.message : "Failed to load touchpoint data");
            setData([]);
            setTotalMessages(0);
            setTotalTouchpoints(0);
            setAvailableCountries([]);
            setSqlQuery(undefined);
        } finally {
            setIsLoading(false);
        }
    }, [dateRange, selectedCountries, selectedDomains, entraOid, client]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Custom tooltip component
    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div
                    style={{
                        backgroundColor: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                    }}
                >
                    <Text size="sm" fw={500}>
                        {data.touchpoint}
                    </Text>
                    <Text size="xs" c="dimmed">
                        Messages: {data.message_count.toLocaleString()}
                    </Text>
                    <Text size="xs" c="dimmed">
                        Percentage: {data.percentage}%
                    </Text>
                    <Text size="xs" c="dimmed">
                        Users: {data.unique_users.toLocaleString()}
                    </Text>
                </div>
            );
        }
        return null;
    };

    // Custom label function for pie slices
    const renderLabel = (props: any) => {
        const { payload } = props;
        return payload && payload.percentage > 5 ? `${payload.percentage}%` : "";
    };

    // Prepare data for recharts with colors
    const chartData = data.map((item, index) => ({
        ...item,
        fill: colors[index % colors.length]
    }));

    const stats =
        showStats && totalMessages > 0 ? (
            <Group gap="xs">
                <Badge color="blue" variant="light" size="sm">
                    {totalMessages.toLocaleString()} Messages
                </Badge>
                <Badge color="green" variant="light" size="sm">
                    {totalTouchpoints} Touchpoints
                </Badge>
                {entraOid && (
                    <Badge color="orange" variant="light" size="sm">
                        User Specific
                    </Badge>
                )}
            </Group>
        ) : null;

    const controls = (
        <Group gap={6} style={{ flexWrap: "nowrap" }}>
            <DateRangeControl value={dateRange} onChange={setDateRange} disabled={!!error} />
            <CountryFilterControl
                value={selectedCountries}
                onChange={setSelectedCountries}
                availableCountries={availableCountries}
                disabled={!!error}
                loading={isLoading}
            />
            <DomainFilterControl
                value={selectedDomains}
                onChange={setSelectedDomains}
                availableDomains={availableDomains}
                disabled={!!error}
                loading={isLoading}
            />
            <ActionIcon variant="light" size="sm" onClick={loadData} loading={isLoading} title="Refresh data" disabled={!!error}>
                <MdRefresh size={16} />
            </ActionIcon>
        </Group>
    );

    // Check if any filters are applied (different from default state)
    const defaultDateRange = createDefaultDateRange(defaultDaysBack);
    const isDateRangeModified = dateRange[0].getTime() !== defaultDateRange[0].getTime() || dateRange[1].getTime() !== defaultDateRange[1].getTime();
    const hasFiltersApplied = isDateRangeModified || selectedCountries.length > 0 || selectedDomains.length > 0;

    const handleResetFilters = () => {
        setDateRange(createDefaultDateRange(defaultDaysBack));
        setSelectedCountries([]);
        setSelectedDomains([]);
    };

    const info = (
        <Group justify="space-between" align="center">
            <div></div>
            <Group gap="xs">
                <Text size="xs" c="dimmed">
                    {formatDateRange(dateRange)}
                </Text>
                <TimezoneIndicator size="xs" />
            </Group>
        </Group>
    );

    return (
        <ChartScaffold
            title={title}
            showTitle={showTitle}
            loading={isLoading}
            error={error}
            empty={!error && data.length === 0}
            emptyMessage="No touchpoint data found for the selected time range."
            stats={stats}
            controls={controls}
            info={info}
            height={height}
            onRefresh={loadData}
            onResetFilters={handleResetFilters}
            showResetFilters={hasFiltersApplied}
            infoContent="Shows message distribution across different touchpoints (apps and platforms). Track usage patterns and identify which channels users prefer for interactions."
            sqlQuery={sqlQuery}
            chartData={chartData}
            showDeveloperTools={true}
        >
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderLabel}
                        outerRadius={Math.min(height * 0.35, 120)}
                        fill="#8884d8"
                        dataKey="message_count"
                        stroke="#fff"
                        strokeWidth={2}
                    >
                        {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    {showLegend && <Legend verticalAlign="bottom" height={36} formatter={(value, entry: any) => `${entry.payload.touchpoint}`} />}
                </PieChart>
            </ResponsiveContainer>
        </ChartScaffold>
    );
};
